import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Card, PrimaryButton } from "@/components/common";
import { MessageSquare, Flag, Trash2, Users, Send, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Community() {
  const { user, hasRole } = useAuth();
  const [posts, setPosts] = useState(null);
  const [text, setText] = useState("");
  const [sport, setSport] = useState("");
  const [posting, setPosting] = useState(false);
  const isMod = hasRole("ops", "admin");

  const load = () => api.get("/community/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setPosting(true);
    try { await api.post("/community/posts", { text, sport }); setText(""); setSport(""); toast.success("Posted"); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setPosting(false); }
  };
  const report = async (id) => { try { await api.post(`/community/posts/${id}/report`); toast.success("Reported to moderators"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { try { await api.delete(`/community/posts/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const moderate = async (id, hide) => { try { await api.post(`/community/posts/${id}/moderate`, { status: hide ? "hide" : "show" }); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!posts) return <Spinner />;

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Community" subtitle="Share updates with the SPORA1 sports community" />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="mb-4">
            <form onSubmit={submit}>
              <textarea data-testid="community-post-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={1000}
                placeholder="Share a result, event highlight or shout-out…"
                className="w-full rounded-lg border border-input bg-background p-3 text-sm outline-none focus:border-primary" />
              <div className="mt-2 flex items-center justify-between gap-2">
                <input data-testid="community-post-sport" value={sport} onChange={(e) => setSport(e.target.value)} placeholder="Sport (optional)"
                  className="w-40 rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
                <PrimaryButton data-testid="community-post-btn" type="submit" disabled={posting}><Send className="h-4 w-4" /> Post</PrimaryButton>
              </div>
            </form>
          </Card>

          {posts.length === 0 ? (
            <Card className="text-center text-sm text-muted-foreground">No posts yet. Start the conversation!</Card>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <Card key={p.id} data-testid={`community-post-${p.id}`} className={p.hidden ? "opacity-60" : ""}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 font-display text-sm font-bold text-primary">
                        {(p.author_name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{p.author_name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{(p.created_at || "").slice(0, 10)}{p.sport ? ` · ${p.sport}` : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {p.hidden && <span className="rounded bg-rose-500/15 px-2 py-0.5 text-[10px] font-mono uppercase text-rose-300">hidden</span>}
                      <button data-testid={`report-post-${p.id}`} onClick={() => report(p.id)} title="Report" className="rounded-lg p-1.5 text-muted-foreground hover:text-amber-400"><Flag className="h-4 w-4" /></button>
                      {(isMod || p.author_id === user?.id) && (
                        <button onClick={() => del(p.id)} title="Delete" className="rounded-lg p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                      )}
                      {isMod && (
                        <button onClick={() => moderate(p.id, !p.hidden)} title={p.hidden ? "Unhide" : "Hide"} className="rounded-lg p-1.5 text-muted-foreground hover:text-primary"><Shield className="h-4 w-4" /></button>
                      )}
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-sm">{p.text}</p>
                  {isMod && p.reports > 0 && <p className="mt-2 text-xs text-amber-400">{p.reports} report(s)</p>}
                </Card>
              ))}
            </div>
          )}
        </div>

        <div>
          <Card>
            <div className="mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Clubs & Groups</h3></div>
            <p className="text-sm text-muted-foreground">Discover and start communities from the <span className="text-primary">Discover</span> tab.</p>
          </Card>
          {isMod && (
            <Card className="mt-4">
              <div className="mb-2 flex items-center gap-2"><Shield className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Moderation</h3></div>
              <p className="text-sm text-muted-foreground">Posts with 3+ reports are auto-hidden. You can hide/unhide or delete any post.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
